import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { Download, FileText, DollarSign, TrendingUp, Calendar, CheckCircle, Settings } from 'lucide-react';
import { Button, LoadingSpinner, ErrorMessage, Card } from '@/core/components';
import { usePermissions } from '@/core/hooks';
import { usePayrollSummary, usePayRuns } from '../hooks';
import { formatCurrency, formatDate } from '../utils';

interface FinanceSettings {
  defaultBillingRate: number;
  mileageRate: number;
  holidayPayPercent: number;
  overtimeThresholdHours: number;
  overtimeMultiplier: number;
}

interface ProfitRow {
  clientId: string;
  clientName: string;
  revenue: number;
  payrollCost: number;
  grossProfit: number;
  marginPercent: number;
}

function isoDate(date: Date): string {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
}

function currentMonthStart(): string {
  const date = new Date();
  return isoDate(new Date(date.getFullYear(), date.getMonth(), 1));
}

function currentMonthEnd(): string {
  const date = new Date();
  return isoDate(new Date(date.getFullYear(), date.getMonth() + 1, 0));
}

export const PayrollReports: React.FC = () => {
  const { can } = usePermissions();
  const [selectedPeriod, setSelectedPeriod] = useState<string>('current-quarter');
  const [periodStart, setPeriodStart] = useState(currentMonthStart());
  const [periodEnd, setPeriodEnd] = useState(currentMonthEnd());
  const [payDate, setPayDate] = useState(isoDate(new Date(Date.now() + 5 * 24 * 60 * 60 * 1000)));
  const [settings, setSettings] = useState<FinanceSettings>({
    defaultBillingRate: 32,
    mileageRate: 0.45,
    holidayPayPercent: 12.07,
    overtimeThresholdHours: 40,
    overtimeMultiplier: 1.5,
  });
  const [profitRows, setProfitRows] = useState<ProfitRow[]>([]);
  const [isSavingSettings, setIsSavingSettings] = useState(false);
  const [isGeneratingPayroll, setIsGeneratingPayroll] = useState(false);

  const { data: summary, isLoading: summaryLoading, error: summaryError } = usePayrollSummary();
  const { data: payRunData, isLoading: runsLoading } = usePayRuns();

  useEffect(() => {
    void loadFinanceControls();
  }, [periodStart, periodEnd]);

  const loadFinanceControls = async () => {
    const [settingsResponse, profitResponse] = await Promise.all([
      fetch('/api/finance/settings', { credentials: 'include' }),
      fetch(`/api/finance/profit-by-client?periodStart=${periodStart}&periodEnd=${periodEnd}`, { credentials: 'include' }),
    ]);
    if (settingsResponse.ok) {
      setSettings(await settingsResponse.json());
    }
    if (profitResponse.ok) {
      const payload = await profitResponse.json();
      setProfitRows(payload.items || []);
    }
  };

  const saveFinanceSettings = async () => {
    setIsSavingSettings(true);
    try {
      const response = await fetch('/api/finance/settings', {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(settings),
      });
      const payload = await response.json();
      if (!response.ok) throw new Error(payload.error || 'Failed to save finance settings');
      setSettings(payload);
      toast.success('Finance rules saved');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to save finance settings');
    } finally {
      setIsSavingSettings(false);
    }
  };

  const generatePayrollFromCheckins = async () => {
    setIsGeneratingPayroll(true);
    try {
      const response = await fetch('/api/payroll/generate-from-checkins', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ periodStart, periodEnd, payDate }),
      });
      const payload = await response.json();
      if (!response.ok) throw new Error(payload.error || 'Failed to generate payroll');
      toast.success(`Payroll generated for ${payload.payStubs?.length ?? 0} carer(s) from verified check-ins`);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to generate payroll');
    } finally {
      setIsGeneratingPayroll(false);
    }
  };

  const downloadAccountingCsv = (target: 'generic' | 'quickbooks' | 'xero') => {
    window.open(`/api/accounting/export.csv?target=${target}&periodStart=${periodStart}&periodEnd=${periodEnd}`, '_blank', 'noopener,noreferrer');
  };

  if (summaryLoading || runsLoading) {
    return (
      <div className="flex justify-center items-center py-12">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (summaryError) {
    return (
      <ErrorMessage
        message={(summaryError as Error).message || 'Failed to load payroll reports'}
      />
    );
  }

  const reports = [
    {
      title: 'Payroll Summary Report',
      description: 'Comprehensive payroll summary for the selected period',
      icon: FileText,
      color: 'bg-blue-100 text-blue-600',
    },
    {
      title: 'Payroll Deductions Report',
      description: 'PAYE, National Insurance, pension and other deduction liabilities',
      icon: DollarSign,
      color: 'bg-green-100 text-green-600',
    },
    {
      title: 'Deduction Summary',
      description: 'Summary of all deductions by category',
      icon: TrendingUp,
      color: 'bg-purple-100 text-purple-600',
    },
    {
      title: 'Deduction Tracking',
      description: 'Track authorised payroll deductions and payments',
      icon: Calendar,
      color: 'bg-orange-100 text-orange-600',
    },
    {
      title: 'Pension Contribution Report',
      description: 'Employee and employer pension contributions',
      icon: TrendingUp,
      color: 'bg-indigo-100 text-indigo-600',
    },
    {
      title: 'P60 Preview',
      description: 'Preview year-end P60 forms (year-end only)',
      icon: FileText,
      color: 'bg-red-100 text-red-600',
    },
  ];

  const handleDownloadReport = (reportTitle: string) => {
    // In a real implementation, this would trigger a report download
    console.log('Downloading report:', reportTitle, 'for period:', selectedPeriod);
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Payroll Reports</h1>
          <p className="text-gray-600 mt-1">
            Generate and download payroll reports
          </p>
        </div>
      </div>

      <Card>
        <div className="p-6 space-y-5">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
            <div>
              <div className="flex items-center gap-2">
                <Settings className="h-5 w-5 text-[#6c3a80]" />
                <h2 className="text-xl font-bold text-gray-900">Billing & Payroll Production Rules</h2>
              </div>
              <p className="mt-2 max-w-3xl text-sm leading-6 text-gray-600">
                Payroll is generated only from valid carer check-in/check-out evidence. Billing exports use verified visit invoices and payroll stubs for accounting.
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button variant="outline" leftIcon={<Download className="h-4 w-4" />} onClick={() => downloadAccountingCsv('quickbooks')}>
                QuickBooks CSV
              </Button>
              <Button variant="outline" leftIcon={<Download className="h-4 w-4" />} onClick={() => downloadAccountingCsv('xero')}>
                Xero CSV
              </Button>
              {can('payroll:write') && (
                <Button leftIcon={<CheckCircle className="h-4 w-4" />} onClick={generatePayrollFromCheckins} disabled={isGeneratingPayroll}>
                  {isGeneratingPayroll ? 'Generating...' : 'Generate From Check-ins'}
                </Button>
              )}
            </div>
          </div>

          <div className="grid gap-4 md:grid-cols-3 xl:grid-cols-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Period start</label>
              <input type="date" value={periodStart} onChange={(event) => setPeriodStart(event.target.value)} className="w-full rounded-md border border-gray-300 px-3 py-2" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Period end</label>
              <input type="date" value={periodEnd} onChange={(event) => setPeriodEnd(event.target.value)} className="w-full rounded-md border border-gray-300 px-3 py-2" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Pay date</label>
              <input type="date" value={payDate} onChange={(event) => setPayDate(event.target.value)} className="w-full rounded-md border border-gray-300 px-3 py-2" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Mileage GBP/mile</label>
              <input type="number" step="0.01" value={settings.mileageRate} onChange={(event) => setSettings((current) => ({ ...current, mileageRate: Number(event.target.value) }))} className="w-full rounded-md border border-gray-300 px-3 py-2" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Holiday pay %</label>
              <input type="number" step="0.01" value={settings.holidayPayPercent} onChange={(event) => setSettings((current) => ({ ...current, holidayPayPercent: Number(event.target.value) }))} className="w-full rounded-md border border-gray-300 px-3 py-2" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Overtime after hrs</label>
              <input type="number" step="0.5" value={settings.overtimeThresholdHours} onChange={(event) => setSettings((current) => ({ ...current, overtimeThresholdHours: Number(event.target.value) }))} className="w-full rounded-md border border-gray-300 px-3 py-2" />
            </div>
          </div>
          <div className="flex justify-end">
            <Button variant="outline" onClick={saveFinanceSettings} disabled={isSavingSettings}>
              {isSavingSettings ? 'Saving...' : 'Save Rules'}
            </Button>
          </div>
        </div>
      </Card>

      {/* Period Selector */}
      <Card>
        <div className="p-6">
          <h3 className="text-lg font-semibold mb-4">Report Period</h3>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Period
              </label>
              <select
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                value={selectedPeriod}
                onChange={(e) => setSelectedPeriod(e.target.value)}
              >
                <option value="current-quarter">Current Quarter</option>
                <option value="last-quarter">Last Quarter</option>
                <option value="ytd">Year to Date</option>
                <option value="last-year">Last Year</option>
                <option value="custom">Custom Range</option>
              </select>
            </div>
            {selectedPeriod === 'custom' && (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Start Date
                  </label>
                  <input
                    type="date"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    End Date
                  </label>
                  <input
                    type="date"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  />
                </div>
              </>
            )}
          </div>
        </div>
      </Card>

      {/* Summary Statistics */}
      {summary && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <Card>
            <div className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Total Payroll</p>
                  <p className="text-2xl font-bold text-gray-900 mt-1">
                    {formatCurrency(summary.totalGrossPay || 0)}
                  </p>
                </div>
                <DollarSign className="h-8 w-8 text-green-600" />
              </div>
            </div>
          </Card>

          <Card>
            <div className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Deductions</p>
                  <p className="text-2xl font-bold text-gray-900 mt-1">
                    {formatCurrency(summary.totalTaxWithheld || 0)}
                  </p>
                </div>
                <FileText className="h-8 w-8 text-blue-600" />
              </div>
            </div>
          </Card>

          <Card>
            <div className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Caregivers Paid</p>
                  <p className="text-2xl font-bold text-gray-900 mt-1">
                    {summary.totalCaregivers || 0}
                  </p>
                </div>
                <TrendingUp className="h-8 w-8 text-purple-600" />
              </div>
            </div>
          </Card>

          <Card>
            <div className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Total Hours</p>
                  <p className="text-2xl font-bold text-gray-900 mt-1">
                    {(summary.totalHours || 0).toFixed(0)}
                  </p>
                </div>
                <Calendar className="h-8 w-8 text-orange-600" />
              </div>
            </div>
          </Card>
        </div>
      )}

      <Card>
        <div className="p-6">
          <div className="flex items-center justify-between gap-4">
            <div>
              <h2 className="text-xl font-bold text-gray-900">Profit Per Client / Contract</h2>
              <p className="mt-1 text-sm text-gray-600">
                Revenue from verified-visit invoices compared with payroll generated from valid check-in/check-out records.
              </p>
            </div>
            <Button variant="outline" leftIcon={<Download className="h-4 w-4" />} onClick={() => downloadAccountingCsv('generic')}>
              Accounting CSV
            </Button>
          </div>
          <div className="mt-5 overflow-x-auto rounded-lg border border-gray-200">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50">
                <tr className="text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                  <th className="px-4 py-3">Client / Contract</th>
                  <th className="px-4 py-3">Revenue</th>
                  <th className="px-4 py-3">Payroll Cost</th>
                  <th className="px-4 py-3">Profit</th>
                  <th className="px-4 py-3">Margin</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 bg-white">
                {profitRows.length === 0 ? (
                  <tr>
                    <td className="px-4 py-4 text-gray-600" colSpan={5}>No profit rows for this period yet.</td>
                  </tr>
                ) : profitRows.map((row) => (
                  <tr key={row.clientId}>
                    <td className="px-4 py-3 font-medium text-gray-900">{row.clientName}</td>
                    <td className="px-4 py-3 text-gray-700">{formatCurrency(row.revenue)}</td>
                    <td className="px-4 py-3 text-gray-700">{formatCurrency(row.payrollCost)}</td>
                    <td className={`px-4 py-3 font-bold ${row.grossProfit >= 0 ? 'text-emerald-700' : 'text-red-700'}`}>
                      {formatCurrency(row.grossProfit)}
                    </td>
                    <td className="px-4 py-3 text-gray-700">{row.marginPercent.toFixed(1)}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </Card>

      {/* Available Reports */}
      <div>
        <h2 className="text-xl font-bold mb-4">Available Reports</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {reports.map((report, index) => {
            const Icon = report.icon;
            return (
              <Card key={index} className="hover:shadow-md transition-shadow">
                <div className="p-6">
                  <div className={`inline-flex p-3 rounded-lg ${report.color} mb-4`}>
                    <Icon className="h-6 w-6" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    {report.title}
                  </h3>
                  <p className="text-sm text-gray-600 mb-4">
                    {report.description}
                  </p>
                  {can('payroll:read') && (
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        leftIcon={<Download className="h-4 w-4" />}
                        onClick={() => handleDownloadReport(report.title)}
                        className="flex-1"
                      >
                        PDF
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        leftIcon={<Download className="h-4 w-4" />}
                        onClick={() => handleDownloadReport(report.title)}
                        className="flex-1"
                      >
                        CSV
                      </Button>
                    </div>
                  )}
                </div>
              </Card>
            );
          })}
        </div>
      </div>

      {/* Recent Pay Runs */}
      {payRunData && payRunData.items.length > 0 && (
        <Card>
          <div className="p-6">
            <h2 className="text-xl font-bold mb-4">Recent Pay Runs</h2>
            <div className="space-y-3">
              {payRunData.items.slice(0, 5).map((payRun) => (
                <div
                  key={payRun.id}
                  className="flex justify-between items-center border-b pb-3 last:border-b-0"
                >
                  <div>
                    <p className="font-medium text-gray-900">{payRun.runNumber}</p>
                    <p className="text-sm text-gray-600">
                      {formatDate(payRun.payPeriodStartDate)} - {formatDate(payRun.payPeriodEndDate)}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="font-semibold text-gray-900">
                      {formatCurrency(payRun.totalGrossPay)}
                    </p>
                    <p className="text-sm text-gray-600">
                      {payRun.totalCaregivers} caregivers
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </Card>
      )}
    </div>
  );
};
